/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react/prop-types */
/* eslint-disable react/destructuring-assignment */
import { Dropdown } from "frontend/src/components/Menu/Dropdown";
import { Menu } from "frontend/src/components/Menu/Menu";
import { handleLogout } from "_sirius/src/actions/action_helpers";
import { setIsExpanded } from "_sirius/src/actions/navigation_sidebar_actions";
import {
  storeNavBarHeight,
  updateNavBarState,
} from "_sirius/src/actions/header_actions";

import FadingHeader from "Components/common/fading_header";
import { GlobalSearch } from "Components/GlobalSearch";
import GlobalSearchControls, {
  pathsWithGlobalSearch,
} from "Components/GlobalSearchWorkspace/GlobalSearchControls.component";
import { useOptionalCurrentUser } from "frontend/src/utils/hooks";
import { onElementHeightChange } from "Helpers/index";
import { isMobileView } from "Helpers/window_wrapper";
import i18n from "i18next";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useRangerChatStore } from "frontend/src/components/RangerChat/chatStore";
import { useOpenChatPanel } from "./Sidebar/useOpenChatPanel";
import { Authorized } from "./Authorized";
import {
  isAuthorized,
  ORG_MANAGEMENT_CHECKS,
} from "./Authorized/authHelpers";
import CreationMenu from "./creation_menu";
import { UserSettingsMenuItem } from "./SettingsLoader/UserSettingsModal";
import Tooltip from "frontend/src/components/Tooltip/Tooltip";
import { NotificationBell } from "frontend/src/components/NotificationBell";
import { TabName } from "./show/summaryContainer.types";

const CLICK_TRIGGER = ["click"] as ["click"];

export function NavBar() {
  const dispatch = useDispatch();
  const fullName = useSelector((state: any) => state.user.fullName);
  const currentUser = useOptionalCurrentUser();
  const isSuperUser = !!currentUser?.flags.isSuperUser;
  // Owned here (not in the dropdown menu item) so the modal can render OUTSIDE the
  // Dropdown — otherwise a click inside the modal closes the dropdown and unmounts it.
  const canManageOrg =
    !!currentUser &&
    isAuthorized({ user: currentUser, checks: ORG_MANAGEMENT_CHECKS });

  const navigate = useNavigate();
  const openChatPanel = useOpenChatPanel();
  const openConversation = useRangerChatStore(s => s.openConversation);

  function setNavBarHeight() {
    const navbar = document.getElementById("navbar");
    const navbarHeight =
      (navbar &&
        window.getComputedStyle(navbar, null).getPropertyValue("height")) ||
      0;

    const navBarNode = document.getElementById("navbar-follower");

    if (navBarNode) {
      navBarNode.style.height = String(navbarHeight);
    }

    dispatch(storeNavBarHeight(String(navbarHeight)));
  }

  useEffect(() => {
    setNavBarHeight();
    const cleanup = onElementHeightChange(
      document.getElementById("navbar"),
      () => {
        setNavBarHeight();
      }
    );

    return cleanup;
  }, []);

  const { pathname } = useLocation();

  return (
    <FadingHeader
      onChange={
        ((state: any) => {
          dispatch(updateNavBarState(state));
        }) as any
      }
      disable={!isMobileView()}
    >
      <div id="navbar" className="main-navbar">
        <div className="main-navbar__content">
          <div
            className="main-logo-sidebar hidden-md-up"
            onClick={() => dispatch(setIsExpanded(true))}
          >
            <i className="main-logo-sidebar__icon" />
          </div>

          <div
            id="fund-explorer-logo"
            className="main-navbar__logo hidden-xl-down"
            onClick={() => navigate("/")}
            style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
          >
            <img
              src="/sirius_logo_white.png"
              alt="Sirius"
              style={{
                height: "32px",
                width: "auto",
              }}
            />
          </div>

          <GlobalSearch />

          <div className="main-navbar__content-right">
            <Authorized checks={["isDataManager"]}>
              <CreationMenu />
            </Authorized>

            {/* Sits directly left of the user name. The navbar is purple with
                white glyphs (.main-navbar__btn), and its own icons run ~18-20px,
                so both are set here rather than baked into the shared component
                — data-collection's toolbar wants a different scale. */}
            <NotificationBell
              sx={{
                color: "white",
                mr: "22px",
                p: 0.5,
                "& .MuiSvgIcon-root": { fontSize: 20 },
              }}
              // Routed per subject, not per app: a guideline breach belongs on the
              // Guidelines tab, another investment event belongs on the investment
              // itself, and anything unrecognised is left alone rather than sent
              // somewhere invented.
              onOpenSettings={() => navigate("/settings/notifications")}
              onNavigate={({ eventType, subjectType, subjectId }) => {
                if (subjectType === "NOTE") {
                  // Same deep link LLM-chat citations use — the notes page
                  // loads exactly this note regardless of paging or filters.
                  navigate(`/notes?noteId=${subjectId}`);
                  return;
                }
                if (subjectType === "CONVERSATION") {
                  // Chat lives in the sidebar, not at a URL: bring the panel on
                  // screen, then hand the id to the history tab, which owns the
                  // loading. The bell never loads a conversation itself.
                  openChatPanel();
                  openConversation(subjectId);
                  return;
                }
                if (subjectType !== "INVESTMENT") return;
                navigate(
                  eventType === "GUIDELINE_BREACH"
                    ? `/investments/${subjectId}/${TabName.Guidelines}`
                    : `/investments/${subjectId}`
                );
              }}
            />

            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item key="1">
                    <a
                      id="logout-button"
                      className="main-popup-dropdown__item"
                      onClick={handleLogout}
                    >
                      {i18n.t("nav_bar.sign_out")}
                    </a>
                  </Menu.Item>
                  <UserSettingsMenuItem
                    key="2"
                    onOpen={() => navigate("/settings/preferences")}
                  />
                  {/* Direct (conditional) Menu.Item child — Antd tracks hover/
                      active by item, so a wrapper component here collides the
                      hover state with the adjacent Settings item. */}
                  {canManageOrg && (
                    <Menu.Item key="org" data-cy="main-navbar__organization">
                      <a
                        className="main-popup-dropdown__item"
                        onClick={() => navigate("/organization")}
                      >
                        Organization
                      </a>
                    </Menu.Item>
                  )}
                  {isSuperUser && (
                    <Menu.Item key="admin" data-cy="main-navbar__admin">
                      <a
                        className="main-popup-dropdown__item"
                        onClick={() => navigate("/admin")}
                      >
                        Admin
                      </a>
                    </Menu.Item>
                  )}
                </Menu>
              }
              trigger={CLICK_TRIGGER}
              arrow
            >
              <span
                className="cursor-pointer main-navbar__btn hidden-lg-down"
                id="current-user-email"
              >
                {fullName || i18n.t("nav_bar.user_profile")}
                <i className="main-navbar__btn-arrow icon icon--20 icon-arrow" />
              </span>
            </Dropdown>

            <Tooltip
              placement="bottom-end"
              trigger="click"
              overlayClassName="rc-tooltip--no-arrow"
              overlayStyle={{ width: "320px" }}
              title={
                <div id="help-modal" className="main-navbar__help-modal">
                  <p>
                    {i18n.t("nav_bar.contact_support")}
                    <br />
                    {i18n.t("nav_bar.contact_support_number")}
                  </p>
                </div>
              }
            >
              <span
                className="main-navbar__btn main-navbar__btn--help hidden-lg-down"
                id="help-dropdown-button"
              >
                <i
                  className="cursor-pointer fa fa-question-circle-o"
                  aria-hidden="true"
                />
              </span>
            </Tooltip>
          </div>
        </div>

        {/* used for Header component, to define additional sub-header information */}
        <div id="heading-yield" />
        <div id="tabs-yield" />
      </div>
      <div id="navbar-follower" />
      <div id="secondary-menu" />
      {/* Nav bar routing rather than portals 
      https://www.digitalocean.com/community/tutorials/react-using-new-portal-feature-in-react
      My preference is to using routing to determine what goes where up here.
      With portals rarely being the correct way to go.
      Portals make the resulting behavior more confusing than routing.
    */}
      {pathsWithGlobalSearch.includes(pathname) && <GlobalSearchControls />}
    </FadingHeader>
  );
}
